import type * as hast from 'hast';
import pMap from 'p-map';
import { find } from 'unist-util-find';
import { EXIT, visit } from 'unist-util-visit';

import {
    type CardsImageObjectFit,
    type ContentRef,
    type DocumentTableSelectOption,
    VerticalAlignment,
} from '@gitbook/api-client';
import {
    BLOCK_TABLE,
    type BlockTableData,
    paragraph,
    type TableColumnDefinition,
    type TableTextColumnVerticalAlignment,
    type TableView,
} from '@gitbook/doc-core';
import { Block, Fragment, generateKey } from '@gitbook/slate';
import { createOrderIndex } from '@gitbook/util-orderindex';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';

import { visitAsync } from '../../utils';
import type { ParsingOptions } from '../options';
import type { HastToGastTransformMap } from './types';
import { getBooleanAttribute, getEnumAttribute, getNumberAttribute } from './utils';

export const transformBlockTables: HastToGastTransformMap = {
    table: async (node: hast.Element, eat, context, options) => {
        // First row will either be in the thead or the tbody
        const { firstRow, isOnlyRow } = getFirstTableRow(node);

        // A table with no rows does not produce a block.
        if (!firstRow) {
            return [];
        }

        // Test if the first row should be processed as a normal row or as a header
        const useFirstRowAsHeader =
            !isOnlyRow &&
            Boolean(
                // We consider it a header if it doesn't contain formatting
                !find(
                    firstRow,
                    // @ts-expect-error
                    ({ type, tagName }) =>
                        type !== 'text' && tagName !== 'td' && tagName !== 'th' && tagName !== 'tr'
                ) ||
                    // Or if we can find a th with a select tag in it
                    find(
                        firstRow,
                        (child: hast.Element) =>
                            child.type === 'element' &&
                            child.tagName === 'th' &&
                            Boolean(getSelectFromHeaderColumn(child))
                    )
            );

        const fragments: Fragment[] = [];
        const data: BlockTableData = {
            records: {},
            definition: {},
            view: extractTableView(node, { useFirstRowAsHeader }),
            ...(typeof node.properties?.dataFullWidth !== 'undefined'
                ? { fullWidth: node.properties.dataFullWidth !== 'false' }
                : undefined),
        };

        // Extract column definitions
        const orderedColumns = extractColumnKeys(node);
        await forEachColumn(firstRow, orderedColumns, (td, colId) => {
            if (td && useFirstRowAsHeader) {
                const colDef = extractHeaderColumnDefinition(colId, td);
                data.definition[colDef.id] = colDef;
                data.view = extractTableViewForColumn(colId, td, data.view);
            } else {
                data.definition[colId] = {
                    id: colId,
                    type: 'text',
                    title: '',
                    textAlignment: getEnumAttribute(
                        td?.properties,
                        'align',
                        ['left', 'center', 'right'],
                        'left'
                    ),
                    verticalAlignment: getEnumAttribute<TableTextColumnVerticalAlignment>(
                        td?.properties,
                        'vAlign',
                        [VerticalAlignment.Top, VerticalAlignment.Middle, VerticalAlignment.Bottom],
                        undefined
                    ),
                };
                data.view.columns.push(colId);
            }
        });

        let lastOrderIndex: string;
        let rowIndex = 0;

        // Process all rows
        await visitAsync<hast.Element>(
            node,
            (node: hast.Element) => node.tagName === 'tr',
            async (tr) => {
                rowIndex += 1;
                // We skip the first row if it was already processed
                if (rowIndex === 1 && useFirstRowAsHeader) {
                    return;
                }

                const recordId = generateKey();

                data.records[recordId] = {
                    orderIndex: createOrderIndex(lastOrderIndex ? { before: lastOrderIndex } : {}),
                    values: {},
                };

                lastOrderIndex = data.records[recordId].orderIndex;

                await forEachColumn(tr, orderedColumns, async (td, colId) => {
                    const colDef = data.definition[colId];

                    switch (colDef.type) {
                        case 'text': {
                            const nodes = td ? await eat('blocks', td.children, context) : [];
                            const fragment = Fragment.create({
                                nodes: nodes.length > 0 ? nodes : [paragraph.createParagraph()],
                            });

                            fragments.push(fragment);

                            data.records[recordId].values[colId] = fragment.fragmentKey;
                            break;
                        }

                        case 'number':
                        case 'rating':
                            data.records[recordId].values[colId] = td
                                ? Number(extractNodeText(td))
                                : 0;
                            break;

                        case 'checkbox':
                            data.records[recordId].values[colId] = td
                                ? extractNodeText(td) !== 'false'
                                : false;
                            break;

                        case 'select':
                            data.records[recordId].values[colId] = td ? extractNodeOptions(td) : [];
                            break;

                        case 'files': {
                            const refs = await extractContentReferences(td!, options, true);
                            data.records[recordId].values[colId] = refs
                                .map((ref) => (ref.kind === 'file' ? ref.file : null))
                                .filter(filterOutNullable);
                            break;
                        }
                        case 'users': {
                            const refs = await extractContentReferences(td!, options);
                            data.records[recordId].values[colId] = refs
                                .map((ref) => (ref.kind === 'user' ? ref.user : null))
                                .filter(filterOutNullable);
                            break;
                        }
                        case 'content-ref': {
                            const refs = await extractContentReferences(td!, options);
                            data.records[recordId].values[colId] = refs[0] || null;
                            break;
                        }
                        case 'image': {
                            const refs = await extractContentReferences(td!, options, true);
                            const contentRef = refs[0] || null;

                            // Filter to only file/URL refs (image columns don't support page refs)
                            const imageRef =
                                contentRef &&
                                (contentRef.kind === 'file' || contentRef.kind === 'url')
                                    ? contentRef
                                    : null;

                            // Check if this is a cover column with object-fit
                            const objectFit = getEnumAttribute(td!.properties, 'dataObjectFit', [
                                'contain',
                                'fill',
                                'cover',
                            ] as const);

                            if (imageRef && objectFit) {
                                data.records[recordId].values[colId] = {
                                    ref: imageRef,
                                    objectFit: objectFit as CardsImageObjectFit, // Store as new format with objectFit
                                };
                            } else {
                                data.records[recordId].values[colId] = imageRef; // Store as legacy format
                            }
                            break;
                        }
                        default:
                            assertNever(colDef);
                    }
                });
            }
        );

        return [
            Block.create({
                type: BLOCK_TABLE,
                isVoid: true,
                data,
                fragments,
            }),
        ];
    },
};

/**
 * Convert a unified node into a string.
 */
function extractNodeText(node: hast.Element): string {
    let result = '';

    visit(
        node,
        (node) => node.type === 'text',
        (text) => {
            // @ts-ignore
            result += text.value;
        }
    );

    return result.trim();
}

/**
 * Single pass: get first <tr> and detect if the table has exactly one row.
 * Works for <table><tr>, <table><tbody><tr>, and <table><thead><tr>.
 */
export function getFirstTableRow(table: hast.Element) {
    let firstRow: hast.Element | undefined;
    let seen = 0;

    visit(
        table,
        // Check whether this is a <tr> whose parent is table, thead, or tbody
        (n: hast.Node, _index?: number, parent?: hast.Parent): boolean => {
            // check node is an element
            if (n.type !== 'element') {
                return false;
            }

            // check node is a <tr>
            const el = n as hast.Element;
            if (el.tagName !== 'tr') {
                return false;
            }

            // check parent exists and is an element
            if (!parent || (parent as hast.Node).type !== 'element') {
                return false;
            }

            // check parent tag name is table, thead or tbody
            if (!isRowParent(parent)) {
                return false;
            }

            return true;
        },
        // visitor: record first row and stop after finding a second
        (tr: hast.Element) => {
            if (!firstRow) {
                firstRow = tr;
            }
            seen += 1;
            if (seen > 1) {
                return EXIT;
            }
        }
    );

    return { firstRow, isOnlyRow: seen === 1 };
}

/**
 * Determines whether the given node is a table, thead or tbody
 */
function isRowParent(node: hast.Node): node is hast.Element {
    return (
        node.type === 'element' &&
        ['table', 'thead', 'tbody'].includes((node as hast.Element).tagName)
    );
}

/**
 * Parse all data-option spans from a node.
 */
function extractNodeOptions(node: hast.Element): string[] | false {
    const result: string[] = [];

    visit(
        node,
        (node) => {
            // @ts-ignore
            return node.tagName === 'span' && 'dataOption' in node.properties;
        },
        (optionSpan) => {
            // @ts-ignore
            result.push(optionSpan.properties.dataOption);
        }
    );

    return result;
}

/**
 * Iterate over all columns of a row.
 */
async function forEachColumn(
    tr: hast.Element,
    columnKeys: string[],
    callback: (td: hast.Element | null, colId: string, index: number) => Promise<void> | void
) {
    const columns = tr.children.filter(isTableColumn);

    for (let index = 0; index < columnKeys.length; index += 1) {
        const td = columns[index] || null;
        await callback(td, columnKeys[index], index);
    }
}

/**
 * Extract a list of column keys from the table content.
 * It iterates through all the rows to find the row with the most columns.
 */
function extractColumnKeys(table: hast.Element): string[] {
    let maxColumns = 0;

    visit(
        table,
        (node: hast.Element) => node.tagName === 'tr',
        (tr: hast.Element) => {
            // @ts-expect-error
            const countColumns = tr.children.filter((td) => isTableColumn(td)).length;
            maxColumns = Math.max(maxColumns, countColumns);
        }
    );

    return new Array(maxColumns).fill(0).map(() => generateKey());
}

/**
 * Test if an element is a table column.
 */
function isTableColumn(element: hast.Element | hast.Comment | hast.Text): element is hast.Element {
    return element.type === 'element' && (element.tagName === 'td' || element.tagName === 'th');
}

/**
 * Extract table view from the table node.
 */
function extractTableView(
    table: hast.Element,
    options: { useFirstRowAsHeader: boolean }
): TableView {
    const viewType = table.properties?.dataView || 'grid';

    switch (viewType) {
        case 'cards': {
            return {
                type: 'cards',
                columns: [],
                cardSize: getEnumAttribute<'medium' | 'large', 'medium'>(
                    table.properties,
                    'dataCardSize',
                    ['large', 'medium'],
                    'medium'
                ),
                hideColumnTitle: 'dataColumnTitleHidden' in (table.properties || {}),
            };
        }
        default: {
            return {
                type: 'grid',
                columns: [],
                useNewSizing: true,
                hideHeader:
                    getBooleanAttribute(table.properties, 'dataHeaderHidden') ||
                    !options.useFirstRowAsHeader,
            };
        }
    }
}

/**
 * Extract table view parameters from a column node (td).
 */
function extractTableViewForColumn<V extends TableView>(
    colId: string,
    td: hast.Element,
    view: V
): V {
    switch (view.type) {
        case 'cards': {
            const hidden = getBooleanAttribute(td.properties, 'dataHidden');
            const isCover = getBooleanAttribute(td.properties, 'dataCardCover');
            const isTarget = getBooleanAttribute(td.properties, 'dataCardTarget');
            const isCoverDark = getBooleanAttribute(td.properties, 'dataCardCoverDark');

            return {
                ...view,
                ...(isCover ? { coverDefinition: colId } : {}),
                ...(isCoverDark ? { coverDefinitionDark: colId } : {}),
                ...(isTarget ? { targetDefinition: colId } : {}),
                columns: hidden ? view.columns : [...view.columns, colId],
            };
        }
        case 'grid': {
            const hidden = getBooleanAttribute(td.properties, 'dataHidden');
            const columnWidth = getNumberAttribute(td.properties, 'width');
            const viewColumnWidths = view.columnWidths ?? {};

            return {
                ...view,
                columns: hidden ? view.columns : [...view.columns, colId],
                columnWidths: {
                    ...viewColumnWidths,
                    ...(columnWidth ? { [colId]: columnWidth } : {}),
                },
            };
        }
        default:
            assertNever(view);
    }
}

/**
 * Extract table definition from a first row TD.
 */
function extractHeaderColumnDefinition(id: string, td: hast.Element): TableColumnDefinition {
    const type = td.properties?.dataType || 'text';
    const title = extractNodeText(td);

    const selectTag = getSelectFromHeaderColumn(td);
    if (selectTag) {
        return {
            id,
            title,
            type: 'select',
            multiple: extractSelectMultipleFromHeaderColumn(selectTag),
            options: extractOptionsFromHeaderColumn(selectTag),
        };
    }

    switch (type) {
        case 'number':
        case 'checkbox':
            return {
                id,
                title,
                type,
            };
        case 'rating':
            return {
                id,
                title,
                type,
                max: getNumberAttribute(td.properties, 'dataMax', 5),
            };
        case 'users':
            return {
                id,
                title,
                type,
                multiple: getBooleanAttribute(td.properties, 'dataMultiple'),
            };
        case 'content-ref':
            return {
                id,
                title,
                type,
            };
        case 'files':
            return {
                id,
                title,
                type,
            };
        case 'image':
            return {
                id,
                title,
                type,
            };
        default:
            return {
                id,
                title,
                type: 'text',
                textAlignment: getEnumAttribute(
                    td.properties,
                    'align',
                    ['left', 'center', 'right'],
                    'left'
                ),
                verticalAlignment: getEnumAttribute<TableTextColumnVerticalAlignment>(
                    td?.properties,
                    'vAlign',
                    [VerticalAlignment.Top, VerticalAlignment.Middle, VerticalAlignment.Bottom],
                    undefined
                ),
            };
    }
}

async function extractContentReferences(
    element: hast.Element,
    options: ParsingOptions,
    asFiles = false
): Promise<ContentRef[]> {
    const links: string[] = [];

    visit(
        element,
        // @ts-ignore
        (node) => node.type === 'element' && node.tagName === 'a' && node.properties.href,
        (node) => {
            // @ts-ignore
            links.push(node.properties.href);
        }
    );

    const contentReferences: ContentRef[] = [];

    await pMap(links, async (link) => {
        const contentReference = asFiles
            ? await options.resolveFile(link)
            : await options.resolveLink(link);

        if (contentReference) {
            contentReferences.push(contentReference);
        }
    });

    return contentReferences;
}

/**
 * Finds and returns a <select> tag in a <th>
 */
function getSelectFromHeaderColumn(th: hast.Element | null) {
    if (!th) {
        return null;
    }
    return th.children.find(
        (node) => node.type === 'element' && node.tagName === 'select'
    ) as hast.Element | null;
}

/**
 * Hydrates an array of DocumentTableSelectOption from a <select> in the header column
 */
function extractOptionsFromHeaderColumn(
    selectTag: hast.Element | null
): DocumentTableSelectOption[] {
    if (!selectTag) {
        return [];
    }

    return selectTag.children.map((option: hast.Element) => ({
        value: option.properties?.value as string,
        label: option.properties?.label as string,
        color: option.properties?.color as string,
    }));
}

/**
 * Extracts whether a select column allows multiple options to be selected or not.
 * To do so, we check whether the `multiple` attribute is set.
 */
function extractSelectMultipleFromHeaderColumn(selectTag: hast.Element | null): boolean {
    if (!selectTag) {
        return false;
    }

    return !!selectTag?.properties?.multiple;
}
